import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, MessageCircle, ShieldCheck } from 'lucide-react';

export default function AdminOtpConfig() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({
        resend_key: '', sender_email: '',
        wa_access_token: '', wa_phone_number_id: '', wa_template_name: '',
        wa_template_lang: 'en_US', wa_graph_version: 'v21.0',
    });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get('/admin/otp-config');
        setCfg(data);
        setForm((f) => ({
            ...f,
            sender_email: data.sender_email || '',
            wa_phone_number_id: data.wa_phone_number_id || '',
            wa_template_name: data.wa_template_name || '',
            wa_template_lang: data.wa_template_lang || 'en_US',
            wa_graph_version: data.wa_graph_version || 'v21.0',
        }));
    };
    useEffect(() => { load(); }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            // Only send secrets when the admin actually typed a new value.
            if (!payload.resend_key) delete payload.resend_key;
            if (!payload.wa_access_token) delete payload.wa_access_token;
            await api.post('/admin/otp-config', payload);
            toast.success('OTP settings saved.');
            setForm((f) => ({ ...f, resend_key: '', wa_access_token: '' }));
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save settings.');
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="admin-otp-config-page">
            <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Verification</p>
                <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">OTP &amp; Verification</h1>
                <p className="text-zinc-500 mt-1">Configure Email (Resend) and WhatsApp (Meta Cloud API) OTP delivery. Until both are configured, OTPs run in DEV mode (shown on screen during signup).</p>
            </div>

            <Card className="p-6 space-y-4" data-testid="otp-email-card">
                <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#2563EB]" />
                    <h2 className="font-display text-lg font-medium">Email OTP — Resend</h2>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${cfg?.resend_configured ? 'bg-green-100 text-green-800' : 'bg-zinc-200 text-zinc-600'}`} data-testid="resend-status">
                        {cfg?.resend_configured ? 'Configured' : 'Not configured'}
                    </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Resend API key</Label>
                        <Input type="password" placeholder={cfg?.resend_key_preview || 're_...'} value={form.resend_key}
                            onChange={(e) => set('resend_key', e.target.value)} data-testid="resend-key-input" />
                    </div>
                    <div className="space-y-2">
                        <Label>From email (verified sender)</Label>
                        <Input placeholder="noreply@yourdomain.com" value={form.sender_email}
                            onChange={(e) => set('sender_email', e.target.value)} data-testid="sender-email-input" />
                    </div>
                </div>
            </Card>

            <Card className="p-6 space-y-4" data-testid="otp-whatsapp-card">
                <div className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-[#1F9D8A]" />
                    <h2 className="font-display text-lg font-medium">WhatsApp OTP — Meta Cloud API</h2>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${cfg?.wa_configured ? 'bg-green-100 text-green-800' : 'bg-zinc-200 text-zinc-600'}`} data-testid="whatsapp-status">
                        {cfg?.wa_configured ? 'Configured' : 'Not configured'}
                    </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Permanent access token</Label>
                        <Input type="password" placeholder={cfg?.wa_access_token_preview || 'EAAB...'} value={form.wa_access_token}
                            onChange={(e) => set('wa_access_token', e.target.value)} data-testid="wa-token-input" />
                    </div>
                    <div className="space-y-2">
                        <Label>Phone number ID</Label>
                        <Input placeholder="1234567890" value={form.wa_phone_number_id}
                            onChange={(e) => set('wa_phone_number_id', e.target.value)} data-testid="wa-phone-id-input" />
                    </div>
                    <div className="space-y-2">
                        <Label>Approved template name</Label>
                        <Input placeholder="otp_verification" value={form.wa_template_name}
                            onChange={(e) => set('wa_template_name', e.target.value)} data-testid="wa-template-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Template language</Label>
                            <Input placeholder="en_US" value={form.wa_template_lang}
                                onChange={(e) => set('wa_template_lang', e.target.value)} data-testid="wa-lang-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Graph API version</Label>
                            <Input placeholder="v21.0" value={form.wa_graph_version}
                                onChange={(e) => set('wa_graph_version', e.target.value)} data-testid="wa-version-input" />
                        </div>
                    </div>
                </div>
            </Card>

            <div className="flex items-center gap-3">
                <Button onClick={save} disabled={saving} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="otp-save-button">
                    <ShieldCheck className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save settings'}
                </Button>
                {cfg?.updated_at && <span className="text-xs text-zinc-500">Last updated: {new Date(cfg.updated_at).toLocaleString()}</span>}
            </div>
        </div>
    );
}
